import { DEV_ONLY_CATEGORIES, NON_NETWORK_CATEGORIES } from './categorize';

/**
 * Turns the meter's raw counters (./meter.js `snapshotRaw()`) into the report:
 * per-category rows, per-stage WebSocket table, coverage, reconciliation, CSV.
 * Pure — no browser APIs — so the arithmetic is unit-tested.
 *
 * Measurement levels are never mixed:
 *   A  application payload bytes — sizes this code computed itself from data the
 *      page handed to a transport (WebSocket message after serialisation, HTTP
 *      request body).
 *   B  browser-reported bytes — numbers the browser exposes (Resource Timing
 *      transferSize, RTCPeerConnection.getStats()).
 *   C  network-level bytes — never available in-page; only the automation
 *      runner's Chrome netlog provides it (tools/network-meter).
 * Unknown values stay `null`. Percentages are computed per level and per
 * direction, only over rows whose value for that direction is known.
 */

export const LEVEL = Object.freeze({
  A: 'A-application-payload',
  B: 'B-browser-reported',
  C: 'C-network-captured',
});

const MB = 1_000_000;
const MIB = 1_048_576;

const round = (value, digits = 3) =>
  value === null || value === undefined || Number.isNaN(value)
    ? null
    : Math.round(value * 10 ** digits) / 10 ** digits;

const toMB = bytes => (bytes === null ? null : round(bytes / MB, 6));
const toMiB = bytes => (bytes === null ? null : round(bytes / MIB, 6));

// Average Mbps = bytes × 8 / durationSeconds / 1,000,000
export const toMbps = (bytes, durationSeconds) =>
  bytes === null || !durationSeconds ? null : round((bytes * 8) / durationSeconds / MB, 6);

const sumKnown = values => {
  const known = values.filter(value => value !== null && value !== undefined);
  return known.length ? known.reduce((total, value) => total + value, 0) : null;
};

const peakOf = (bucketMap, index) => {
  if (!bucketMap) {
    return null;
  }
  let peak = null;
  for (const pair of Object.values(bucketMap)) {
    const value = pair[index];
    if (typeof value === 'number' && (peak === null || value > peak)) {
      peak = value;
    }
  }
  return peak;
};

const peakMbps = bytesPerSecond => (bytesPerSecond === null ? null : toMbps(bytesPerSecond, 1));

const isApplicationCategory = category =>
  !DEV_ONLY_CATEGORIES.has(category) && !NON_NETWORK_CATEGORIES.has(category);

function wsRows(raw, durationSeconds) {
  return Object.entries(raw.ws ?? {}).map(([category, agg]) => {
    const sent = agg.sent;
    const received = agg.received;
    const count = sent.count + received.count;
    const combined = sent.bytes + received.bytes;
    const buckets = raw.buckets?.[`${LEVEL.A}|${category}`];
    const extensions = agg.extensions?.length ? agg.extensions.join(', ') : 'none negotiated';
    return {
      category,
      source: 'WebSocket message payloads (after serialisation)',
      level: LEVEL.A,
      uploadBytes: sent.bytes,
      downloadBytes: received.bytes,
      combinedBytes: combined,
      count,
      countLabel: `${sent.count} sent / ${received.count} received messages`,
      averageMessageBytes: count ? round(combined / count, 1) : null,
      averageSentMessageBytes: sent.count ? round(sent.bytes / sent.count, 1) : null,
      averageReceivedMessageBytes: received.count
        ? round(received.bytes / received.count, 1)
        : null,
      minSentMessageBytes: sent.min,
      maxSentMessageBytes: sent.max,
      minReceivedMessageBytes: received.min,
      maxReceivedMessageBytes: received.max,
      sentMessagesPerSecond: durationSeconds ? round(sent.count / durationSeconds, 2) : null,
      receivedMessagesPerSecond: durationSeconds
        ? round(received.count / durationSeconds, 2)
        : null,
      peakUploadBytesPerSecond: peakOf(buckets, 0),
      peakDownloadBytesPerSecond: peakOf(buckets, 1),
      connectionsOpened: agg.connectionsOpened,
      reconnections: agg.reconnections,
      closeCodes: agg.closeCodes,
      discardedSends: agg.discardedSends,
      maxBufferedAmount: agg.maxBufferedAmount,
      unknownTypeMessages: agg.unknownTypeMessages,
      includedInTotals: isApplicationCategory(category),
      limitations:
        'Payload bytes only: excludes WebSocket frame headers (2–14 B/frame + 4 B client mask), ' +
        `TCP/IP/TLS overhead and retransmissions. Compression extension: ${extensions}` +
        (agg.extensions?.length ? ' — on-wire bytes may be smaller than payload.' : '.') +
        (DEV_ONLY_CATEGORIES.has(category) ? ' Dev-server only; not in a built bundle.' : ''),
    };
  });
}

function requestBodyRows(raw) {
  return Object.entries(raw.requests ?? {}).map(([category, agg]) => ({
    category,
    source: 'HTTP request bodies (fetch / XHR / sendBeacon wrappers)',
    level: LEVEL.A,
    uploadBytes: agg.requests ? agg.bodyBytes : null,
    downloadBytes: null,
    combinedBytes: null,
    count: agg.requests,
    countLabel: `${agg.requests} requests (${agg.byTransport.fetch} fetch, ${agg.byTransport.xhr} xhr, ${agg.byTransport.beacon} beacon)`,
    averageMessageBytes: null,
    requestBodiesUnmeasurable: agg.bodiesUnmeasurable,
    requestBodyBytesLowerBound: agg.bodyBytesLowerBound,
    failedRequests: agg.failed,
    abortedRequests: agg.aborted,
    httpErrorResponses: agg.httpErrors,
    peakUploadBytesPerSecond: peakOf(raw.buckets?.[`${LEVEL.A}|${category}|http-body`], 0),
    peakDownloadBytesPerSecond: null,
    includedInTotals: isApplicationCategory(category),
    limitations:
      'Body bytes only (no request line/headers/cookies). Response bodies are not read at this ' +
      'level — see the level-B row. ' +
      (agg.bodiesUnmeasurable
        ? `${agg.bodiesUnmeasurable} bodies (stream / Request object) could not be sized. `
        : '') +
      (agg.bodyBytesLowerBound
        ? 'FormData bodies counted as a lower bound (parts only, no multipart boundaries). '
        : '') +
      'Requests made through a pre-captured native fetch (Sentry transport) are missed here.',
  }));
}

function resourceRows(raw) {
  return Object.entries(raw.http ?? {}).map(([category, agg]) => {
    const known = agg.entries - agg.transferSizeUnavailable;
    const cacheSummary = Object.entries(agg.cache)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    return {
      category,
      source: 'PerformanceResourceTiming (transferSize)',
      level: LEVEL.B,
      uploadBytes: null,
      downloadBytes: known > 0 ? agg.transferSize : null,
      combinedBytes: null,
      count: agg.entries,
      countLabel: `${agg.entries} resource entries`,
      averageMessageBytes: null,
      encodedBodySize: agg.encodedBodySize,
      decodedBodySize: agg.decodedBodySize,
      entriesWithSizeUnavailable: agg.transferSizeUnavailable,
      cacheBreakdown: agg.cache,
      viaServiceWorker: agg.viaServiceWorker,
      serviceWorkerDecodedBodySize: agg.serviceWorkerDecodedBodySize ?? null,
      failedOrStatusZero: agg.failed,
      httpErrorResponses: agg.httpErrors,
      redirects: agg.redirects,
      repeatedNetworkDownloads: agg.repeatedNetworkDownloads,
      initiatorTypes: agg.initiatorTypes,
      peakUploadBytesPerSecond: null,
      peakDownloadBytesPerSecond: peakOf(raw.buckets?.[`${LEVEL.B}|${category}`], 1),
      includedInTotals: isApplicationCategory(category),
      limitations:
        'Download only (Resource Timing has no request sizes). transferSize = response headers + ' +
        'encoded body as fetched, excludes TLS/TCP/IP. Entries with all-zero sizes (cross-origin ' +
        'without Timing-Allow-Origin, or unknown) are counted but not summed. Peak is bytes ' +
        `attributed to the second the response ended, not a true rate. Cache: ${cacheSummary || 'n/a'}.` +
        (agg.viaServiceWorker
          ? ` ${agg.viaServiceWorker} entries were answered by the service worker: their transfer size is unavailable in-page (reported as unknown, not 0); the automation runner shows what the SW fetched.`
          : '') +
        (DEV_ONLY_CATEGORIES.has(category)
          ? ' Dev-server only: unbundled modules, not what a built bundle downloads.'
          : ''),
    };
  });
}

function rtcRows(raw) {
  const rtc = raw.rtc;
  if (!rtc || !rtc.connections?.length) {
    return [];
  }
  const totals = rtc.totals;
  const buckets = raw.buckets?.[`${LEVEL.B}|voice-agent-webrtc`];
  const transportKnown = totals.transportSource !== null;
  const rows = [
    {
      category: 'voice-agent-webrtc',
      source: `getStats() ${totals.transportSource ?? 'transport'} bytesSent/bytesReceived`,
      level: LEVEL.B,
      uploadBytes: transportKnown ? totals.transport.sent : null,
      downloadBytes: transportKnown ? totals.transport.received : null,
      combinedBytes: transportKnown ? totals.transport.sent + totals.transport.received : null,
      count: rtc.connections.length,
      countLabel: `${rtc.connections.length} peer connection(s)`,
      averageMessageBytes: null,
      peakUploadBytesPerSecond: peakOf(buckets, 0),
      peakDownloadBytesPerSecond: peakOf(buckets, 1),
      counterResets: rtc.connections.reduce((total, pc) => total + pc.counterResets, 0),
      includedInTotals: true,
      limitations:
        'ICE transport payload bytes (SRTP audio + RTCP + DTLS/SCTP data channel), excluding ' +
        'IP/UDP headers and ICE connectivity checks. Sampled once per second: up to ~1 s of ' +
        'traffic after the last sample before a connection closes is missed. Breakdown rows below ' +
        'are part of this total and are not added again.',
    },
    {
      category: 'voice-agent-webrtc: inbound audio (RTP)',
      source: 'getStats() inbound-rtp kind=audio bytesReceived + headerBytesReceived',
      level: LEVEL.B,
      uploadBytes: totals.outboundAudio.payloadBytes + totals.outboundAudio.headerBytes,
      downloadBytes: totals.inboundAudio.payloadBytes + totals.inboundAudio.headerBytes,
      combinedBytes: null,
      count: totals.inboundAudio.packets,
      countLabel: `${totals.inboundAudio.packets} inbound RTP packets, ${totals.outboundAudio.packets} outbound`,
      averageMessageBytes: totals.inboundAudio.packets
        ? round(
            (totals.inboundAudio.payloadBytes + totals.inboundAudio.headerBytes) /
              totals.inboundAudio.packets,
            1
          )
        : null,
      rtpPayloadBytesReceived: totals.inboundAudio.payloadBytes,
      rtpHeaderBytesReceived: totals.inboundAudio.headerBytes,
      peakUploadBytesPerSecond: null,
      peakDownloadBytesPerSecond: null,
      includedInTotals: false,
      partOf: 'voice-agent-webrtc',
      limitations:
        'Subset of the transport row (not added again). Excludes SRTP auth tags and RTCP.',
    },
    {
      category: 'voice-agent-webrtc: data channel (oai-events)',
      source: 'getStats() data-channel bytesSent/bytesReceived',
      level: LEVEL.B,
      uploadBytes: totals.dataChannel.bytesSent,
      downloadBytes: totals.dataChannel.bytesReceived,
      combinedBytes: totals.dataChannel.bytesSent + totals.dataChannel.bytesReceived,
      count: totals.dataChannel.messagesSent + totals.dataChannel.messagesReceived,
      countLabel: `${totals.dataChannel.messagesSent} sent / ${totals.dataChannel.messagesReceived} received messages`,
      averageMessageBytes: null,
      peakUploadBytesPerSecond: null,
      peakDownloadBytesPerSecond: null,
      includedInTotals: false,
      partOf: 'voice-agent-webrtc',
      limitations: 'Message payload bytes; subset of the transport row (not added again).',
    },
  ];
  return rows;
}

function mediaRow(raw) {
  const media = raw.media;
  if (!media || !media.available || !media.elementsSeen) {
    return [];
  }
  return [
    {
      category: 'media-elements (decoder bytes)',
      source: 'HTMLMediaElement webkitVideoDecodedByteCount + webkitAudioDecodedByteCount',
      level: LEVEL.B,
      uploadBytes: null,
      downloadBytes: media.decodedBytes,
      combinedBytes: null,
      count: media.elementsSeen,
      countLabel: `${media.elementsSeen} <video>/<audio> elements with a URL source`,
      averageMessageBytes: null,
      peakUploadBytesPerSecond: null,
      peakDownloadBytesPerSecond: null,
      includedInTotals: false,
      partOf: 'media-video / media-audio',
      limitations:
        'Chromium-only proxy: compressed bytes handed to the decoder, not transfer bytes. Shown ' +
        'because Resource Timing often reports only the first range request of a <video>. Not ' +
        'added to any total (overlaps the media rows).',
    },
  ];
}

function withRates(row, durationSeconds) {
  return {
    ...row,
    uploadMB: toMB(row.uploadBytes),
    downloadMB: toMB(row.downloadBytes),
    combinedMB: toMB(row.combinedBytes),
    uploadMiB: toMiB(row.uploadBytes),
    downloadMiB: toMiB(row.downloadBytes),
    averageUploadMbps: toMbps(row.uploadBytes, durationSeconds),
    averageDownloadMbps: toMbps(row.downloadBytes, durationSeconds),
    peakUploadMbps: peakMbps(row.peakUploadBytesPerSecond ?? null),
    peakDownloadMbps: peakMbps(row.peakDownloadBytesPerSecond ?? null),
  };
}

function levelTotals(rows, level) {
  const included = rows.filter(row => row.level === level && row.includedInTotals);
  const upload = sumKnown(included.map(row => row.uploadBytes));
  const download = sumKnown(included.map(row => row.downloadBytes));
  const bothKnown = included.filter(row => row.uploadBytes !== null && row.downloadBytes !== null);
  return {
    level,
    rows: included.length,
    uploadBytes: upload,
    downloadBytes: download,
    uploadMB: toMB(upload),
    downloadMB: toMB(download),
    rowsWithUnknownUpload: included.filter(row => row.uploadBytes === null).length,
    rowsWithUnknownDownload: included.filter(row => row.downloadBytes === null).length,
    combinedBytesOverRowsWithBothDirections: sumKnown(
      bothKnown.map(row => row.uploadBytes + row.downloadBytes)
    ),
  };
}

function addPercentages(rows, totalsByLevel) {
  return rows.map(row => {
    const totals = totalsByLevel[row.level];
    if (!row.includedInTotals || !totals) {
      return {
        ...row,
        uploadPercentOfLevel: null,
        downloadPercentOfLevel: null,
        combinedPercentOfLevel: null,
      };
    }
    const pct = (value, total) =>
      value === null || !total ? null : round((value / total) * 100, 2);
    return {
      ...row,
      uploadPercentOfLevel: pct(row.uploadBytes, totals.uploadBytes),
      downloadPercentOfLevel: pct(row.downloadBytes, totals.downloadBytes),
      combinedPercentOfLevel:
        row.uploadBytes === null || row.downloadBytes === null
          ? null
          : pct(
              row.uploadBytes + row.downloadBytes,
              totals.combinedBytesOverRowsWithBothDirections
            ),
    };
  });
}

/** Stage durations inside the interval, from the timeline the page reported. */
export function stageDurations(stageTimeline, durationMs) {
  const durations = {};
  (stageTimeline ?? []).forEach((entry, index, list) => {
    const end = index + 1 < list.length ? list[index + 1].atMs : durationMs;
    durations[entry.stage] = (durations[entry.stage] ?? 0) + Math.max(0, end - entry.atMs);
  });
  return durations;
}

function stageRows(raw, durationMs) {
  const durations = stageDurations(raw.stageTimeline, durationMs);
  const rows = [];
  Object.entries(raw.ws ?? {}).forEach(([category, agg]) => {
    Object.entries(agg.stages ?? {}).forEach(([stage, counts]) => {
      const seconds = durations[stage] ? durations[stage] / 1000 : null;
      rows.push({
        category,
        stage,
        stageSeconds: seconds === null ? null : round(seconds, 1),
        sentMessages: counts.sent.count,
        receivedMessages: counts.received.count,
        sentBytes: counts.sent.bytes,
        receivedBytes: counts.received.bytes,
        sentMB: toMB(counts.sent.bytes),
        receivedMB: toMB(counts.received.bytes),
        sentMessagesPerSecond: seconds ? round(counts.sent.count / seconds, 2) : null,
        receivedMessagesPerSecond: seconds ? round(counts.received.count / seconds, 2) : null,
        averageUploadMbps: toMbps(counts.sent.bytes, seconds),
        averageDownloadMbps: toMbps(counts.received.bytes, seconds),
        averageSentMessageBytes: counts.sent.count
          ? round(counts.sent.bytes / counts.sent.count, 1)
          : null,
        averageReceivedMessageBytes: counts.received.count
          ? round(counts.received.bytes / counts.received.count, 1)
          : null,
      });
    });
  });
  return rows;
}

function reconciliation(rows, raw) {
  const checks = [];
  const grand = raw.grandTotals ?? {};
  [LEVEL.A, LEVEL.B].forEach(level => {
    const included = rows.filter(row => row.level === level && row.includedInTotals);
    ['upload', 'download'].forEach(direction => {
      const key = `${direction}Bytes`;
      const sumOfRows = sumKnown(included.map(row => row[key]));
      const independent = grand[level]?.[direction] ?? null;
      checks.push({
        level,
        direction,
        sumOfCategoryRows: sumOfRows,
        independentlyAccumulatedTotal: independent,
        match:
          sumOfRows === null && (independent === null || independent === 0)
            ? true
            : sumOfRows === independent,
      });
    });
  });
  return {
    checks,
    allMatch: checks.every(check => check.match),
    method:
      'Each recorder adds every byte twice at record time: once to its category and once to a ' +
      'per-level grand total. The report re-sums the category rows and compares. Breakdown rows ' +
      '(RTP audio, data channel, media decoder bytes) and dev-only/non-network categories are ' +
      'excluded from both sides, so nothing is counted twice.',
  };
}

function coverage(raw) {
  const has = value => value !== null && value !== undefined;
  const rtcUsed = Boolean(raw.rtc?.connections?.length);
  const items = [
    {
      source: 'Pose/evaluation WebSocket',
      level: LEVEL.A,
      status: raw.ws?.['pose-evaluation-ws'] ? 'measured' : 'not observed in interval',
      note: 'Exact UTF-8/binary payload sizes of every message; stage from STAGE_STATUS.',
    },
    {
      source: 'Other WebSockets',
      level: LEVEL.A,
      status: Object.keys(raw.ws ?? {}).some(key => key !== 'pose-evaluation-ws')
        ? 'measured'
        : 'none observed',
      note: 'Any socket the page constructs after the meter is installed.',
    },
    {
      source: 'Voice agent (WebRTC audio + oai-events data channel)',
      level: LEVEL.B,
      status: rtcUsed ? 'measured' : 'not used in interval',
      note: 'getStats() deltas, 1 s sampling. Mic is never opened (recvonly), so upload is RTCP/DTLS/data channel only.',
    },
    {
      source: 'Voice setup HTTP (/voice-companion/call/, register-call/)',
      level: `${LEVEL.A} + ${LEVEL.B}`,
      status:
        raw.requests?.['voice-signaling-http'] || raw.http?.['voice-signaling-http']
          ? 'measured'
          : 'not observed in interval',
      note: 'SDP offer body (A) and response transferSize (B).',
    },
    {
      source: 'HTTP/API request bodies',
      level: LEVEL.A,
      status: 'measured (partial)',
      note: 'fetch/XHR/sendBeacon made through the page globals. Headers not included.',
    },
    {
      source: 'HTTP responses, static assets, images, fonts, media, analytics',
      level: LEVEL.B,
      status: 'measured (partial)',
      note: 'Main-thread Resource Timing. Cross-origin entries without Timing-Allow-Origin report no sizes.',
    },
    {
      source: 'Pose model + WASM fetched inside the pose worker',
      level: LEVEL.B,
      status: raw.http?.['pose-model']
        ? 'measured (main-thread fallback path)'
        : 'unavailable in-page',
      note:
        'Requests made by a dedicated worker land in the performance timeline of that worker, which the ' +
        'page cannot read. See meta.cacheState (service-worker cache contents at start/stop) and the ' +
        'automation runner (level C) for these bytes.',
    },
    {
      source: 'Service-worker cache hits vs network',
      level: LEVEL.B,
      status: 'partial',
      note: 'Entries answered by mediapipe-sw.js are flagged, but cache-vs-network inside the SW is not exposed.',
    },
    {
      source: 'Sentry envelopes',
      level: `${LEVEL.B} only`,
      status: 'partial',
      note: 'Sentry sends with a native fetch captured before the wrapper, so request bodies are unavailable; responses appear in Resource Timing.',
    },
    {
      source: 'Actual network bytes (TCP/UDP/TLS)',
      level: LEVEL.C,
      status: 'unavailable in-page',
      note: 'Use tools/network-meter/run-measurement.mjs (Chrome netlog) for level C.',
    },
    {
      source: 'HTTP request/response headers, cookies, WebSocket frame headers',
      level: 'any',
      status: 'unavailable',
      note: 'Response headers are inside transferSize (B); request headers are never exposed.',
    },
    {
      source: 'Media element decoder bytes',
      level: LEVEL.B,
      status:
        has(raw.media?.decodedBytes) && raw.media?.available ? 'measured (proxy)' : 'unavailable',
      note: 'Chromium-only; reported separately, not added to totals.',
    },
  ];
  return items;
}

export function buildReport(raw) {
  const durationMs = raw.meta?.durationMs ?? 0;
  const durationSeconds = durationMs / 1000;

  const baseRows = [
    ...wsRows(raw, durationSeconds),
    ...requestBodyRows(raw),
    ...resourceRows(raw),
    ...rtcRows(raw),
    ...mediaRow(raw),
  ].map(row => withRates(row, durationSeconds));

  const totalsByLevel = {
    [LEVEL.A]: levelTotals(baseRows, LEVEL.A),
    [LEVEL.B]: levelTotals(baseRows, LEVEL.B),
  };
  const rows = addPercentages(baseRows, totalsByLevel);

  const ranked = rows
    .filter(row => row.includedInTotals)
    .map(row => ({
      category: row.category,
      level: row.level,
      bytes: sumKnown([row.uploadBytes, row.downloadBytes]),
    }))
    .filter(entry => entry.bytes !== null)
    .sort((left, right) => right.bytes - left.bytes);

  const topUrls = Object.entries(raw.httpUrls ?? {})
    .map(([url, agg]) => ({ url, ...agg, transferMB: toMB(agg.transferSize) }))
    .sort((left, right) => right.transferSize - left.transferSize)
    .slice(0, 25);

  return {
    schema: 'rehabitaire-network-meter/1',
    meta: {
      ...raw.meta,
      durationSeconds: round(durationSeconds, 3),
      totalsLabel: 'partial measured total — see coverage; not all application traffic',
    },
    levels: {
      [LEVEL.A]: 'Application payload bytes computed by the page (not internet consumption).',
      [LEVEL.B]: 'Browser-reported bytes (Resource Timing, getStats).',
      [LEVEL.C]: 'Network-captured bytes — not available in-page; see automation runner.',
    },
    totalsByLevel: Object.fromEntries(
      Object.entries(totalsByLevel).map(([level, totals]) => [
        level,
        {
          ...totals,
          averageUploadMbps: toMbps(totals.uploadBytes, durationSeconds),
          averageDownloadMbps: toMbps(totals.downloadBytes, durationSeconds),
        },
      ])
    ),
    rows,
    stageRows: stageRows(raw, durationMs),
    stageTimeline: raw.stageTimeline ?? [],
    wsConnections: raw.wsConnections ?? [],
    rtcConnections: raw.rtc?.connections ?? [],
    highestConsumers: ranked.slice(0, 10),
    topHttpUrls: topUrls,
    coverage: coverage(raw),
    reconciliation: reconciliation(rows, raw),
  };
}

const CSV_COLUMNS = [
  'category',
  'level',
  'source',
  'uploadBytes',
  'uploadMB',
  'downloadBytes',
  'downloadMB',
  'combinedBytes',
  'combinedMB',
  'count',
  'countLabel',
  'averageMessageBytes',
  'averageUploadMbps',
  'averageDownloadMbps',
  'peakUploadMbps',
  'peakDownloadMbps',
  'uploadPercentOfLevel',
  'downloadPercentOfLevel',
  'combinedPercentOfLevel',
  'includedInTotals',
  'limitations',
];

const csvCell = value => {
  if (value === null || value === undefined) {
    return 'unavailable';
  }
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function reportToCsv(report) {
  const lines = [CSV_COLUMNS.join(',')];
  report.rows.forEach(row => {
    lines.push(CSV_COLUMNS.map(column => csvCell(row[column])).join(','));
  });
  lines.push('');
  const stageColumns = [
    'category',
    'stage',
    'stageSeconds',
    'sentMessages',
    'receivedMessages',
    'sentBytes',
    'receivedBytes',
    'sentMessagesPerSecond',
    'receivedMessagesPerSecond',
    'averageUploadMbps',
    'averageDownloadMbps',
  ];
  lines.push(stageColumns.join(','));
  report.stageRows.forEach(row => {
    lines.push(stageColumns.map(column => csvCell(row[column])).join(','));
  });
  return lines.join('\n');
}
